import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, where,
  onSnapshot,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import { invalidateEmployeeUsersCache } from './attendanceDb';

// Fetch all pending users
export const fetchPendingUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'pending_users'));
    const pending = [];
    querySnapshot.forEach((doc) => {
      pending.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return pending;
  } catch (error) {
    console.error('Error fetching pending users:', error);
    return [];
  }
};

// Real-time listener for pending users
export const onPendingUsersChange = (callback) => {
  const q = query(collection(db, 'pending_users'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const pending = [];
    snapshot.forEach((doc) => {
      pending.push({ ...doc.data(), id: doc.id });
    });
    callback(pending);
  }, (error) => {
    console.error('Error listening to pending users:', error);
  });
};

// Approve user
export const approveUser = async (email, department, hrEmail, name = "") => {
  try {
    const userRef = doc(db, 'users', email);
    await setDoc(userRef, {
      email,
      name,
      role: 'EMPLOYEE',
      department,
      status: 'APPROVED',
      leaveBalance: { casual: 1 },
      createdAt: new Date().toISOString(),
      approvedBy: hrEmail,
      approvedAt: new Date().toISOString()
    });

    // Delete from pending
    await deleteDoc(doc(db, 'pending_users', email));
    invalidateEmployeeUsersCache();
    return true;
  } catch (error) {
    console.error('Error approving user:', error);
    throw error;
  }
};

// Reject user
export const rejectUser = async (email, hrEmail) => {
  try {
    const pendingRef = doc(db, 'pending_users', email);
    await updateDoc(pendingRef, {
      status: 'REJECTED',
      rejectedBy: hrEmail,
      rejectedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error rejecting user:', error);
    throw error;
  }
};

// Fetch system users
export const fetchSystemUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return users;
  } catch (error) {
    console.error('Error fetching system users:', error);
    return [];
  }
};

// Real-time listener for system users
export const onSystemUsersChange = (callback) => {
  const q = query(collection(db, 'users'), orderBy('role'));
  return onSnapshot(q, (snapshot) => {
    const users = [];
    snapshot.forEach((doc) => {
      users.push({ ...doc.data(), id: doc.id });
    });
    callback(users);
  }, (error) => {
    console.error('Error listening to system users:', error);
  });
};

/**
 * Set a user's role (HR or EMPLOYEE). Caller must already be HR (Firestore rules).
 * First HR bootstrap: Firebase Console → Firestore → users → {email} → role = "HR"
 */
export const updateUserRole = async (email, role) => {
  const normalized = String(role).toUpperCase();
  if (normalized !== 'HR' && normalized !== 'EMPLOYEE') {
    throw new Error('Role must be HR or EMPLOYEE');
  }
  const userRef = doc(db, 'users', email);
  if (normalized === 'HR') {
    const settingsRef = SETTINGS_APP();
    const settingsSnap = await getDoc(settingsRef);
    let heads = { ...(settingsSnap.data()?.departmentHeads || {}) };
    Object.keys(heads).forEach((k) => {
      if (heads[k] === email) delete heads[k];
    });
    await setDoc(
      settingsRef,
      { departmentHeads: heads, updatedAt: new Date().toISOString() },
      { merge: true }
    );
  }

  await setDoc(
    userRef,
    {
      email,
      role: normalized,
      status: 'APPROVED',
      updatedAt: new Date().toISOString(),
      ...(normalized === 'HR'
        ? { headOfDepartment: deleteField(), headOfDepartments: deleteField() }
        : {}),
    },
    { merge: true }
  );
  invalidateEmployeeUsersCache();
  return true;
};

// Update system user department
export const updateSystemUser = async (email, department) => {
  try {
    const userRef = doc(db, 'users', email);
    await updateDoc(userRef, {
      department,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Error updating system user:', error);
    throw error;
  }
};

const SETTINGS_APP = () => doc(db, 'settings', 'app');

/** Departments this user heads (supports legacy single `headOfDepartment` string). */
export const normalizeHeadDepartmentsFromUser = (user) => {
  if (!user) return [];
  const arr = user.headOfDepartments;
  if (Array.isArray(arr) && arr.length) {
    return [...new Set(arr.filter(Boolean))];
  }
  if (user.headOfDepartment && typeof user.headOfDepartment === 'string') {
    return [user.headOfDepartment];
  }
  return [];
};

/** Department on a leave row: stored empDept or looked up from users list. */
export function resolveLeaveDepartment(leave, employees = []) {
  if (!leave) return '';
  const fromField = String(leave.empDept || '').trim();
  if (fromField) return fromField;
  const em = leave.eEmail;
  if (!em || !employees?.length) return '';
  const row = employees.find((e) => e.email === em || e.id === em);
  return String(row?.department || '').trim();
}

/**
 * Whether this head should see the leave on Team Leaves: pending from their dept, or pending_head routed to them.
 * Includes leaves that went to HR as `pending` if the employee's department matches a headed department.
 */
export function leaveNeedsDepartmentHeadAction(leave, currentUser, employees = []) {
  const me = currentUser?.email;
  if (!leave || !me || leave.eEmail === me) return false;
  const headed = normalizeHeadDepartmentsFromUser(currentUser);
  if (!headed.length) return false;
  const headSet = new Set(headed.map((d) => String(d).trim().toLowerCase()));
  const empDept = resolveLeaveDepartment(leave, employees);
  const dk = empDept.toLowerCase();
  if (!dk || !headSet.has(dk)) return false;
  if (leave.status === 'pending') return true;
  if (leave.status === 'pending_head') {
    if (leave.headEmail && leave.headEmail !== me) return false;
    return true;
  }
  return false;
}

/**
 * Resolve head email from settings/app.departmentHeads (same map HR updates when assigning a head).
 * Trim + case-insensitive key match so employee.department still matches after assigning a head.
 */
export const getHeadEmailForDepartment = async (department) => {
  if (!department || !db) return null;
  try {
    const snap = await getDoc(SETTINGS_APP());
    if (!snap.exists()) return null;
    const heads = snap.data().departmentHeads || {};
    const want = String(department).trim();
    if (!want) return null;
    if (heads[want]) return heads[want];
    const wantLower = want.toLowerCase();
    const key = Object.keys(heads).find((k) => String(k).trim().toLowerCase() === wantLower);
    return key ? heads[key] : null;
  } catch (e) {
    console.error('getHeadEmailForDepartment', e);
    return null;
  }
};

/**
 * One head per department in settings/app.departmentHeads; an employee may head multiple departments.
 * Clears other users who were head of any of those departments, then assigns this user.
 */
export const setUserHeadOfDepartments = async (email, departments) => {
  const normalized = [...new Set((departments || []).filter(Boolean))];
  const settingsRef = SETTINGS_APP();
  const settingsSnap = await getDoc(settingsRef);
  let heads = { ...(settingsSnap.data()?.departmentHeads || {}) };

  Object.keys(heads).forEach((k) => {
    if (heads[k] === email) delete heads[k];
  });

  for (const dept of normalized) {
    const [snapLegacy, snapArr] = await Promise.all([
      getDocs(query(collection(db, 'users'), where('headOfDepartment', '==', dept))),
      getDocs(query(collection(db, 'users'), where('headOfDepartments', 'array-contains', dept))),
    ]);
    const byId = new Map();
    snapLegacy.docs.forEach((d) => byId.set(d.id, d));
    snapArr.docs.forEach((d) => byId.set(d.id, d));

    for (const d of byId.values()) {
      if (d.id === email) continue;
      const data = d.data();
      const patch = { updatedAt: new Date().toISOString() };
      if (data.headOfDepartment === dept) {
        patch.headOfDepartment = null;
      }
      if (Array.isArray(data.headOfDepartments) && data.headOfDepartments.includes(dept)) {
        const next = data.headOfDepartments.filter((x) => x !== dept);
        patch.headOfDepartments = next.length ? next : deleteField();
      }
      if (Object.keys(patch).length > 1) {
        await updateDoc(doc(db, 'users', d.id), patch);
      }
      if (heads[dept] === d.id) delete heads[dept];
    }
    heads[dept] = email;
  }

  const userRef = doc(db, 'users', email);
  const userPatch = { updatedAt: new Date().toISOString() };
  if (normalized.length) {
    userPatch.headOfDepartments = normalized;
    userPatch.headOfDepartment = deleteField();
  } else {
    userPatch.headOfDepartments = deleteField();
    userPatch.headOfDepartment = deleteField();
  }
  await updateDoc(userRef, userPatch);

  await setDoc(
    settingsRef,
    { departmentHeads: heads, updatedAt: new Date().toISOString() },
    { merge: true }
  );
  invalidateEmployeeUsersCache();
  return true;
};

// Remove system user
export const removeSystemUser = async (email) => {
  try {
    await deleteDoc(doc(db, 'users', email));
    invalidateEmployeeUsersCache();
    // Optionally log out the user or clean up their other data
    return true;
  } catch (error) {
    console.error('Error removing system user:', error);
    throw error;
  }
};

// Real-time listener for approved employees only
export const onApprovedEmployeesChange = (callback) => {
  const q = query(
    collection(db, 'users'), 
    where('role', '==', 'EMPLOYEE'),
    where('status', '==', 'APPROVED')
  );
  return onSnapshot(q, (snapshot) => {
    const users = [];
    snapshot.forEach((doc) => {
      users.push({ ...doc.data(), id: doc.id });
    });
    callback(users);
  }, (error) => {
    console.error('Error listening to approved employees:', error);
  });
};
